import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { transactionApi } from "../../repositories/transaction";
import {
  Users,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Copy,
  Check,
  MoreVertical,
  Mail,
} from "lucide-react";

interface Transaction {
  id: string;
  email: string;
  productId: string;
  transactionId: string;
  status: string;
  pitchLimit: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface CopiedFieldsState {
  [key: string]: boolean;
}

const TransactionPage = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedFields, setCopiedFields] = useState<CopiedFieldsState>({});
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [tableView, setTableView] = useState<"userTransactions" | "allTransactions">("userTransactions");

  const fetchTransactions = async (page: number): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await (tableView === "userTransactions"
        ? transactionApi.getAllUserTransactions({
            page,
            limit,
            search: searchTerm.trim(),
            status: statusFilter !== "all" ? statusFilter : undefined,
          })
        : transactionApi.getAllTransactionHistories({
            page,
            limit,
            search: searchTerm.trim(),
            status: statusFilter !== "all" ? statusFilter : undefined,
          }));
      setTransactions(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setPage(response.data.pagination.page);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiError?.response?.data?.message || "Failed to fetch transactions",
        confirmButtonColor: "#3B82F6",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchTransactions(page);
  }, [page, limit, debouncedSearchTerm, statusFilter, tableView]);

  const handleRefresh = () => {
    fetchTransactions(page);
  };

  const handleLimitChange = (newLimit: any) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleStatusFilterChange = (e: any) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const copyToClipboard = (text: any, field: any) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedFields({ ...copiedFields, [field]: true });
      setTimeout(() => {
        setCopiedFields({ ...copiedFields, [field]: false });
      }, 2000);
    });
  };

  const getStatusBadge = (status: any) => {
    const statusMap: any = {
      completed: { color: "text-green-700", bg: "bg-green-100" },
      inactive: { color: "text-red-700", bg: "bg-red-100" },
      pending: { color: "text-yellow-700", bg: "bg-yellow-100" },
      cancelled: { color: "text-gray-700", bg: "bg-gray-100" },
    };

    const style = statusMap[status.toLowerCase()] || {
      color: "text-blue-700",
      bg: "bg-blue-100",
    };

    return (
      <span
        className={`px-3 py-1 text-xs font-medium rounded-full ${style.color} ${style.bg}`}
      >
        {status}
      </span>
    );
  };

  const truncateText = (text: any, maxLength = 10) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const toggleActionMenu = (id: string) => {
    setOpenActionMenuId(openActionMenuId === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenActionMenuId(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleEdit = (transaction: Transaction) => {
    Swal.fire({
      icon: "info",
      title: "Edit Transaction",
      text: `Editing transaction for ${transaction.email}`,
      confirmButtonColor: "#3B82F6",
    });
    setOpenActionMenuId(null);
  };

  const handleDelete = (transaction: Transaction) => {
    Swal.fire({
      icon: "warning",
      title: "Delete Transaction",
      text: `Are you sure you want to delete ${transaction.email}'s transaction?`,
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Delete",
    });
    setOpenActionMenuId(null);
  };

  const handleChangeStatus = (transaction: Transaction, newStatus: string) => {
    Swal.fire({
      icon: "info",
      title: "Change Status",
      text: `Changing status for ${transaction.email} to ${newStatus}`,
      confirmButtonColor: "#3B82F6",
    });
    setOpenActionMenuId(null);
  };

  const handleSendEmail = (transaction: Transaction) => {
    Swal.fire({
      icon: "info",
      title: "Send Email",
      text: `Preparing to send email to ${transaction.email}`,
      confirmButtonColor: "#3B82F6",
    });
    setOpenActionMenuId(null);
  };

  const handleToggleTableView = () => {
    setTableView((prev) =>
      prev === "userTransactions" ? "allTransactions" : "userTransactions"
    );
    setPage(1);
  };

  return (
    <div className="min-h-screen">
      <div className="p-6 mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">

              <h1 className="text-2xl font-medium text-gray-800">
                {tableView === "userTransactions"
                  ? "User Transaction Management"
                  : "All Transaction Histories"}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
            
              <button
                onClick={handleRefresh}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 transition bg-white rounded-md shadow-sm hover:bg-gray-50"
                disabled={isLoading}
              >
                <RefreshCw
                  size={16}
                  className={`mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              <button className="flex items-center px-3 py-2 text-sm font-medium text-white transition bg-blue-600 rounded-md shadow-sm hover:bg-blue-700">
                <Download size={16} className="mr-2" />
                Export
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-6">
              <div className="flex w-full">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="w-full py-2 pl-10 pr-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center md:col-span-6 md:justify-end">
              <div className="flex items-center mr-4">
                <div className="relative inline-block w-40">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Filter size={16} className="text-gray-500" />
                  </div>
                  <select
                    className="w-full py-2 pl-10 pr-8 text-sm bg-white border border-gray-300 rounded-md appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                  >
                    <option value="all">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <span className="mr-2 text-sm text-gray-600">Show:</span>
                <div className="relative inline-block w-20">
                  <select
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={limit}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[500px] bg-white rounded-lg shadow">
            <RefreshCw size={32} className="text-blue-600 animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow min-h-[500px]">
            <Users size={48} className="mb-4 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-700">
              No Transactions Found
            </h2>
            <p className="text-gray-500">
              Try adjusting your search criteria or add new transactions.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden bg-white rounded-lg shadow min-h-[500px] flex flex-col">
              <div className="p-4">
                <div className="relative inline-flex items-center max-w-sm bg-white border border-gray-300 rounded-md shadow-sm">
                <button
                  onClick={handleToggleTableView}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md focus:outline-none ${
                    tableView === "userTransactions"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  User Transactions
                </button>
                <button
                  onClick={handleToggleTableView}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md focus:outline-none ${
                    tableView === "allTransactions"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  All Transactions
                </button>
              </div>
              </div>
            <div className="flex-grow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Product ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Transaction ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap"
                    >
                      Pitch Limit
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Created At
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((sub) => (
                    <tr key={sub.id} className="transition hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {sub.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {sub.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="max-w-[120px] truncate"
                            title={sub.productId}
                          >
                            {truncateText(sub.productId, 14)}
                          </div>
                          <button
                            onClick={() =>
                              copyToClipboard(sub.productId, `productId-${sub.id}`)
                            }
                            className="p-1 ml-2 text-gray-500 transition hover:text-blue-600"
                            title="Copy to clipboard"
                          >
                            {copiedFields[`productId-${sub.id}`] ? (
                              <Check size={16} className="text-green-600" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="max-w-[120px] truncate"
                            title={sub.transactionId}
                          >
                            {truncateText(sub.transactionId, 14)}
                          </div>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                sub.transactionId,
                                `transactionId-${sub.id}`
                              )
                            }
                            className="p-1 ml-2 text-gray-500 transition hover:text-blue-600"
                            title="Copy to clipboard"
                          >
                            {copiedFields[`transactionId-${sub.id}`] ? (
                              <Check size={16} className="text-green-600" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(sub.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {sub.pitchLimit}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {new Date(sub.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <div
                          className="relative"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => toggleActionMenu(sub.id)}
                            className="p-1 text-gray-500 transition rounded hover:bg-gray-100"
                          >
                            <MoreVertical size={18} />
                          </button>
                          {openActionMenuId === sub.id && (
                            <div className="absolute right-0 z-10 w-48 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                              <div className="py-1">
                                <button
                                  onClick={() => handleSendEmail(sub)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Mail size={16} className="mr-2 text-blue-600" />
                                  Send Email
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {transactions.length > 0 &&
                    transactions.length < 10 &&
                    [...Array(10 - transactions.length)].map((_, index) => (
                      <tr key={`empty-${index}`} className="h-14">
                        <td colSpan={8} className="px-6 py-4"></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center text-sm text-gray-700">
                <p className="hidden md:block">
                  Showing{" "}
                  <span className="font-medium">{(page - 1) * limit + 1}</span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(page * limit, totalPages * limit)}
                  </span>{" "}
                  of <span className="font-medium">{totalPages * limit}</span>{" "}
                  results
                </p>
              </div>

              <div className="flex items-center">
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">First</span>
                    <ChevronLeft size={16} className="mr-1" />
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-3 py-2 -ml-px text-sm font-medium bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft size={16} />
                  </button>

                  <div className="hidden md:flex">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      const isCurrentPage = pageNum === page;

                      return (
                        <button
                          key={i}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            isCurrentPage
                              ? "z-10 bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {totalPages > 5 && (
                      <>
                        <span className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300">
                          ...
                        </span>
                        <button
                          onClick={() => setPage(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-3 py-2 -ml-px text-sm font-medium bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-3 py-2 -ml-px text-sm font-medium bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Last</span>
                    <ChevronRight size={16} />
                    <ChevronRight size={16} className="ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionPage;